import React, { useContext, useEffect, useRef, useState } from "react";
import { ExpressCheckoutElement, useElements, useStripe } from "@stripe/react-stripe-js";
import {
	StripeExpressCheckoutElementClickEvent,
	StripeExpressCheckoutElementConfirmEvent,
	StripeExpressCheckoutElementReadyEvent,
	StripeExpressCheckoutElementShippingAddressChangeEvent,
	StripeExpressCheckoutElementShippingRateChangeEvent,
} from "@stripe/stripe-js";
import BasketContext from "../services/BasketContext";
import ExpressCheckoutService from "../services/ExpressCheckoutService";
import useNotification from "./notification/useNotification";
import NotificationType from "./notification/NotificationType";
import GCPaymentProvider from "../../../../Site.Checkout/src/src/paymentDetailsStep/GCPaymentProviders.enum";
import Logger from "../../../gulp/scripts/typescript/modules/logger";
import { ISaveDeliveryAddressResponse } from "../../../../Site.Checkout/src/src/models/ISaveDeliveryAddressResponse.model";
import IShippingMethod from "../../../../Site.Checkout/src/src/models/IShippingMethod.model";
import PaymentOverlay from "./PaymentOverlay";
import { isCompleteBillingDetails, isCompleteContactDetails, isChannelIslandsPostcode, isCompleteShippingAddress } from "../helpers/expressCheckoutAddress";

declare var dataLayer: any;

function StripeExpressCheckout(props: { onAvailabilityChange?: (isAvailable: boolean) => void }) {
	const stripe = useStripe();
	const elements = useElements();
	const basketContext = useContext(BasketContext);
	const expressCheckoutService = new ExpressCheckoutService();
	const { addNotification } = useNotification();
	const [isSaving, setIsSaving] = useState(false);
	// Item + tax only until onShippingAddressChange/onShippingRateChange negotiate a real delivery
	// option - initialisePayment in onConfirm must use this, not basketContext.basket.Total/SubTotal.
	const [currentTotalAmount, setCurrentTotalAmount] = useState(
		basketContext?.basket.SubTotal.Price ?? 0,
	);

	// Started eagerly on mount, not on click - Google Pay can open its sheet and get an address
	// fast enough to lose the race against a session started later, showing a generic "Invalid  shipping address" even though our response eventually arrives correctly.
	const sessionInitPromiseRef = useRef<Promise<any> | null>(null);
	const [isSessionReady, setIsSessionReady] = useState(false);
	const [isWalletAvailable, setIsWalletAvailable] = useState<boolean | null>(null);

	function onReady(event: StripeExpressCheckoutElementReadyEvent) {
		setIsWalletAvailable(!!event?.availablePaymentMethods);
	}

	useEffect(() => {
		if (isWalletAvailable !== null) {
			props.onAvailabilityChange?.(isWalletAvailable);
		}
	}, [isWalletAvailable]);

	useEffect(() => {
		sessionInitPromiseRef.current = new ExpressCheckoutService().init();
		sessionInitPromiseRef.current
			.then(() => setIsSessionReady(true))
			.catch((error) => onError(error, "ExpressStripeWallet - unexpected error initialising session."));
	}, []);

	useEffect(() => {
		setCurrentTotalAmount(basketContext?.basket.SubTotal.Price ?? 0);
	}, [basketContext?.basket.SubTotal.Price]);

	function getGA4Items() {
		return basketContext?.basket.LineItems.map((lineItem) => ({
			...lineItem.ViewItemEventInfo,
			quantity: lineItem.Quantity
		}));
	}

	function trackBeginCheckout(paymentType: string) {
		dataLayer = dataLayer || [];
		dataLayer.push({ ecommerce: null });
		dataLayer.push({
			event: "begin_checkout",
			ecommerce: {
				value: currentTotalAmount,
				items: getGA4Items()
			}
		});
	}

	function trackAddPaymentInfo(paymentType: string) {
		dataLayer = dataLayer || [];
		dataLayer.push({ ecommerce: null });
		dataLayer.push({
			event: "add_payment_info",
			ecommerce: {
				payment_type: paymentType,
				value: currentTotalAmount,
				items: getGA4Items()
			}
		});
	}

	function onClick(event: StripeExpressCheckoutElementClickEvent) {
		trackBeginCheckout(event.expressPaymentType);
		event.resolve();
	}

	// Labels which specific await failed once it reaches the generic outer catch in onConfirm -
	// init()/initialisePayment()/completeOrder() would otherwise all report the same "unexpected
	// error in onConfirm" with no way to tell which one actually threw.
	function labelError(label: string) {
		return (error: any) => {
			throw new Error(`[${label}] ${error?.message || String(error)}`, {
				cause: error,
			});
		};
	}

	function onError(error: Error, additionalExceptionErrorMessage?: string) {
		setIsSaving(false);
		Logger.trackException(error, additionalExceptionErrorMessage);

		addNotification({
			headline: "There was a problem with your express checkout payment method, please try again or use an alternative payment method.",
			type: NotificationType.error,
			autoDismissMilliseconds: 0
		});
	}

	async function onShippingAddressChange(event: StripeExpressCheckoutElementShippingAddressChangeEvent) {
		try {
			// TEMP: only allow GB addresses while we fix other issues
			if (event.address?.country !== "GB" || isChannelIslandsPostcode(event.address?.postal_code)) {
				Logger.trackTrace(`ExpressStripeWallet - rejected non-GB shipping address (${event.address?.country} ${event.address?.postal_code}).`);
				event.reject();
				return;
			}

			await (sessionInitPromiseRef.current ??= expressCheckoutService.init());

			const saveResponse = await expressCheckoutService.saveCoarseAddress({
				city: event.address?.city,
				postcode: event.address?.postal_code,
				countryCode: event.address?.country,
				isStripeExpressCheckout: true,
			});
			const deliveryOptions = (saveResponse as ISaveDeliveryAddressResponse)?._embedded?.deliveryOptions;
			if (!deliveryOptions?.deliveryOptions?.length) {
				event.reject();
				return;
			}
			const total = deliveryOptions?._embedded?.summary?.total;
			if (typeof total?.amount === "number") {
				setCurrentTotalAmount(total.amount);
			}
			if (elements && typeof total?.penceAmount === "number") {
				elements.update({ amount: total.penceAmount });
			}
			// Express checkout builds its shipping-rate list from every ShippingMethod across the (single, non-splitting) delivery option's shipping groups, instead of from the top-level delivery-option list
			const shippingMethods = deliveryOptions.deliveryOptions.reduce((methods, option) => {
				(option.shippingGroups ?? []).forEach((group) => {
					methods.push(...(group.groupedShippingMethods ?? []));
				});
				return methods;
			}, [] as IShippingMethod[]);
			if (!shippingMethods.length) {
				event.reject();
				return;
			}
			// Stripe has no per-rate "selected" flag - per its own docs, "The first shipping rate
			// listed appears in the payment interface as the default option." So the previously-chosen
			// method (backend's isChecked, set from the basket's persisted ShippingMethodId) needs to
			// be moved to the front, or the wallet always defaults back to whichever method the backend
			// happens to list first (e.g. after a page refresh) regardless of what was actually chosen.
			const orderedShippingMethods = [...shippingMethods].sort(
				(a, b) => Number(b.isChecked) - Number(a.isChecked),
			);
			event.resolve({
				shippingRates: orderedShippingMethods.map((method) => ({
					id: String(method.shippingMethodId),
					displayName: method.estimateLabel ? `${method.name} ${method.estimateLabel}` : method.name,
					amount: Math.round((method.price ?? 0) * 100),
				})),
			});
		} catch (error) {
			event.reject();
			onError(
				error as Error,
				"ExpressStripeWallet - unexpected error in onShippingAddressChange.",
			);
		}
	}

	async function onShippingRateChange(event: StripeExpressCheckoutElementShippingRateChangeEvent) {
		try {
			const saveResponse = await expressCheckoutService.saveDeliveryOptions({
				selectedDeliveryOption: event.shippingRate?.id,
			});
			const total =
				saveResponse?._embedded?.deliveryOptions?._embedded?.summary?.total;
			if (typeof total?.amount === "number") {
				setCurrentTotalAmount(total.amount);
			}
			if (elements && typeof total?.penceAmount === "number") {
				elements.update({ amount: total.penceAmount });
			}
			event.resolve();
		} catch (error) {
			event.reject();
			onError(
				error as Error,
				"ExpressStripeWallet - unexpected error in onShippingRateChange.",
			);
		}
	}

	async function onConfirm(event: StripeExpressCheckoutElementConfirmEvent) {
		if (!stripe || !elements) {
			onError(
				new Error(
					"Stripe or Elements not available in ExpressStripeWallet onConfirm.",
				),
			);
			return;
		}

		setIsSaving(true);
		Logger.trackTrace("ExpressStripeWallet - onConfirm called.");

		try {
			const { error: submitError } = await elements.submit();
			if (submitError) {
				onError(
					new Error(submitError.message, { cause: submitError }),
					"ExpressStripeWallet - elements.submit() failed.",
				);
				return;
			}

			if (!isCompleteBillingDetails(event.billingDetails)) {
				setIsSaving(false);
				Logger.trackTrace("ExpressStripeWallet - rejected incomplete billing address from wallet contact.");
				event.paymentFailed({
					reason: "invalid_billing_address",
					message: "Please add a full billing address, including cardholder name, street, city, country and, for UK deliveries, your postcode.",
				});
				return;
			}
			if (!isCompleteShippingAddress(event.shippingAddress)) {
				setIsSaving(false);
				Logger.trackTrace("ExpressStripeWallet - rejected incomplete shipping address from wallet contact.");
				event.paymentFailed({
					reason: "invalid_shipping_address",
					message: "Please add a full delivery address, including recipient name, street, city, country and, for UK deliveries, your postcode.",
				});
				return;
			}
			if (!isCompleteContactDetails(event.billingDetails)) {
				setIsSaving(false);
				Logger.trackTrace("ExpressStripeWallet - rejected incomplete contact details from wallet contact.");
				event.paymentFailed({
					reason: "invalid_payment_data",
					message: "Please provide a valid email address and phone number.",
				});
				return;
			}

			// TEMP: only allow GB addresses while we fix other issues - re-checked here as a final check
			const shippingCountry = event.shippingAddress?.address?.country;
			const shippingPostcode = event.shippingAddress?.address?.postal_code;
			if (shippingCountry !== "GB" || isChannelIslandsPostcode(shippingPostcode)) {
				setIsSaving(false);
				Logger.trackTrace(`ExpressStripeWallet - rejected non-GB shipping address at confirm (${shippingCountry} ${shippingPostcode}).`);
				event.paymentFailed({
					reason: "invalid_shipping_address",
					message: "Please use the full checkout for delivery to addresses outside of mainland UK. Please click Go to Checkout to continue.",
				});
				return;
			}

			await (
				sessionInitPromiseRef.current ??= expressCheckoutService.init()
			).catch(labelError("init"));
			const email = event.billingDetails?.email ?? "";

			const initialiseResponse = await expressCheckoutService
				.initialisePayment({
					selectedPaymentMethodName: GCPaymentProvider.Stripe,
					paymentAmount: currentTotalAmount,
					email,
				})
				.catch(labelError("initialisePayment"));

			if (!initialiseResponse.success || !initialiseResponse.reference) {
				onError(
					new Error(
						initialiseResponse.errorMessages?.join("\n") ||
							"Failed to initialise Stripe payment.",
					),
				);
				return;
			}

			const shippingAddress = event.shippingAddress!;
			const confirmPaymentResponse = await stripe.confirmPayment({
				elements,
				clientSecret: initialiseResponse.reference,
				redirect: "if_required",
				confirmParams: {
					return_url: window.location.href,
					shipping: {
						name: shippingAddress.name,
						address: {
							line1: shippingAddress.address.line1,
							line2: shippingAddress.address.line2 ?? undefined,
							city: shippingAddress.address.city,
							state: shippingAddress.address.state,
							postal_code: shippingAddress.address.postal_code,
							country: shippingAddress.address.country,
						},
					},
				},
			});

			if (confirmPaymentResponse.error) {
				onError(
					new Error(confirmPaymentResponse.error.message, {
						cause: confirmPaymentResponse.error,
					}),
					"ExpressStripeWallet - stripe.confirmPayment() failed.",
				);
				return;
			}

			const stripePaymentMethodId =
				confirmPaymentResponse.paymentIntent?.payment_method?.toString();
			if (!stripePaymentMethodId) {
				onError(
					new Error(
						"No Stripe payment method ID returned from confirmPayment.",
					),
				);
				return;
			}

			trackAddPaymentInfo(event.expressPaymentType);

			const completeOrderResponse = await expressCheckoutService
				.completeOrder({
					email,
					selectedPaymentMethodName: GCPaymentProvider.Stripe,
					stripePaymentMethodId,
				})
				.catch(labelError("completeOrder"));

			if (completeOrderResponse.success) {
				Logger.trackTrace(
					`ExpressStripeWallet - complete order successful ${completeOrderResponse.successRedirectUrl}.`,
				);
				window.location.replace(completeOrderResponse.successRedirectUrl);
			} else {
				onError(
					new Error(
						`ExpressStripeWallet - complete order failed: ${completeOrderResponse.failureCustomerMessage}.`,
					),
				);
			}
		} catch (error) {
			onError(
				error as Error,
				"ExpressStripeWallet - unexpected error in onConfirm.",
			);
		}
	}

	if (isWalletAvailable === false) {
		return null;
	}

	// Stays invisible/collapsed (not unmounted - onReady still needs to run) rather than showing a
	// loader here: we can't tell in advance whether this will resolve to available or not, and a
	// loader that flashes then disappears for the common no-wallet case is worse than no loader at all.
	const isVisible = isSessionReady && isWalletAvailable === true;

	return (
		<div
			className={`c-basket__express-stripe-wallet${isVisible ? "" : " c-basket__express-stripe-wallet--pending"}`}
			data-test={isVisible ? "express-stripe-wallet" : "express-stripe-wallet-pending"}
		>
			<ExpressCheckoutElement
				options={{
					buttonType: { applePay: "plain", googlePay: "plain" },
					shippingAddressRequired: true,
					emailRequired: true,
					phoneNumberRequired: true,
					billingAddressRequired: true,
					buttonHeight: 46,
				}}
				onReady={onReady}
				onClick={onClick}
				onShippingAddressChange={onShippingAddressChange}
				onShippingRateChange={onShippingRateChange}
				onConfirm={onConfirm}
			/>
			{isSaving ? <PaymentOverlay /> : null}
		</div>
	);
}

export default StripeExpressCheckout;
